import type {
  CashDenominationConfidence,
  CashDenominationSource,
} from './cash-denominations.dto.js';
import type { CashLocationType } from './cash-locations.dto.js';
import type { CashMovementDirection, CashMovementKind } from './cash-movement.dto.js';
import type {
  CashReportActorRefDto,
  CashReportCashLocationRefDto,
  CashReportMovementCategory,
  CashReportPurchasePayoutRefDto,
  CashReportSessionRefDto,
  CashReportSourceRefDto,
  CashReportVarianceActionRefDto,
  CashReportVarianceCaseRefDto,
} from './cash-report-common.dto.js';

export const cashDenominationUsageCategoryValues = [
  'usage',
  'usageReversal',
  'internalMovement',
  'supply',
  'varianceMovement',
  'unsupported',
] as const;
export type CashDenominationUsageCategory = (typeof cashDenominationUsageCategoryValues)[number];

export interface GetCashDenominationUsageReportRequest {
  companyId: string;
  from: string;
  to: string;
  storeId?: string;
  locationId?: string;
  stationId?: string;
  cashLocationId?: string;
  cashLocationKind?: CashLocationType;
  usageCategory?: CashDenominationUsageCategory;
  movementKind?: CashMovementKind;
  reportCategory?: CashReportMovementCategory;
  source?: CashDenominationSource;
  confidence?: CashDenominationConfidence;
  denominationMinor?: number;
}

export interface CashDenominationUsageDenominationRowDto {
  denominationMinor: number;
  count: number;
  amountMinor: number;
}

export interface CashDenominationUsageSummaryRowDto {
  denominationMinor: number;
  usageCategory: CashDenominationUsageCategory;
  movementKind: CashMovementKind;
  reportCategory: CashReportMovementCategory;
  cashLocation: CashReportCashLocationRefDto;
  movementCount: number;
  allocatedMovementCount: number;
  grossInCount: number;
  grossOutCount: number;
  netCount: number;
  grossInAmountMinor: number;
  grossOutAmountMinor: number;
  netDepletionAmountMinor: number;
}

export interface CashDenominationUsageCoverageRowDto {
  usageCategory: CashDenominationUsageCategory;
  movementKind: CashMovementKind;
  reportCategory: CashReportMovementCategory;
  cashLocation: CashReportCashLocationRefDto;
  totalMovementCount: number;
  allocatedMovementCount: number;
  amountOnlyMovementCount: number;
  missingRowsMovementCount: number;
  unsupportedMovementCount: number;
  totalAmountMinor: number;
  allocatedAmountMinor: number;
  amountOnlyAmountMinor: number;
  missingRowsAmountMinor: number;
  unsupportedAmountMinor: number;
}

export interface CashDenominationUsageMovementRowDto {
  movementId: string;
  companyId: string;
  occurredAt: string;
  direction: CashMovementDirection;
  amountMinor: number;
  movementKind: CashMovementKind;
  reportCategory: CashReportMovementCategory;
  usageCategory: CashDenominationUsageCategory;
  isReversal: boolean;
  cashLocation: CashReportCashLocationRefDto;
  session: CashReportSessionRefDto | null;
  varianceCase: CashReportVarianceCaseRefDto | null;
  varianceAction: CashReportVarianceActionRefDto | null;
  purchasePayout: CashReportPurchasePayoutRefDto | null;
  source: CashReportSourceRefDto | null;
  actor: CashReportActorRefDto | null;
  allocationId: string | null;
  currentRevisionId: string | null;
  allocationSource: CashDenominationSource | null;
  allocationConfidence: CashDenominationConfidence | null;
  denominationRows: CashDenominationUsageDenominationRowDto[];
}

export interface GetCashDenominationUsageReportResponse {
  companyId: string;
  from: string;
  to: string;
  summaryRows: CashDenominationUsageSummaryRowDto[];
  coverageRows: CashDenominationUsageCoverageRowDto[];
  movementRows: CashDenominationUsageMovementRowDto[];
}
